import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const SWIPER_SRC = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';

/** @type {Promise<any> | null} */
let swiperPromise = null;

/**
 * Resolves with the global `Swiper`, injecting the same CDN build the bundle
 * template uses when no other section has loaded it. Shared by every instance
 * on the page; a failed load is forgotten so a later section render can retry.
 */
function loadSwiper() {
  if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);

  swiperPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SWIPER_SRC;
    script.async = true;
    script.addEventListener('load', () =>
      typeof window.Swiper === 'function' ? resolve(window.Swiper) : reject(new Error('Swiper global missing'))
    );
    script.addEventListener('error', () => reject(new Error(`Failed to load ${SWIPER_SRC}`)));
    document.head.appendChild(script);
  }).catch((error) => {
    swiperPromise = null;
    throw error;
  });

  return swiperPromise;
}

/**
 * Bundle builder + media gallery for `sections/mmx-featured-product.liquid`.
 *
 * Rows are *not* individual cart forms. Each row only records a local quantity;
 * pressing the section CTA sends every non-zero row to `/cart/add.js` as one
 * `{ items: [...] }` payload, mirroring `ProductFormComponent#processBatchAddToCart`
 * in `product-form.js`, then applies the section's discount code through
 * `/cart/update.js` the way `cart-discount.js` does. That ordering matters: the
 * code has to land after the lines exist, otherwise a quantity-gated discount
 * is rejected as inapplicable.
 */
class MmxFeaturedProduct extends HTMLElement {
  /** @type {boolean} */
  #submitting = false;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {number | undefined} */
  #addedTimeout;

  /**
   * Listeners are bound per connection: a section re-render in the theme editor
   * moves the element rather than replacing it, so `connectedCallback` runs again
   * and un-scoped listeners would stack (each Add click counting twice, and so on).
   * @type {AbortController}
   */
  #listeners = new AbortController();

  /** @type {import('swiper').Swiper | null} */
  #heroSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #thumbsSwiper = null;

  connectedCallback() {
    this.#listeners = new AbortController();
    this.#setupGallery();
    this.#setupBundle();
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#destroySwipers();
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  /* ------------------------------- Gallery ------------------------------- */

  /**
   * Mirrors the mm-bundle-builder gallery: a swipeable hero Swiper driven by a
   * thumbnail Swiper (three per view) through Swiper's thumbs module. The Figma
   * prev/next arrows step the hero and wrap at either end.
   *
   * Swiper is not a theme dependency (the bundle template pulls it in through a
   * Custom Liquid block), so it is loaded on demand. Until it arrives — or if it
   * never does — the arrows and thumbs still work against the same markup.
   */
  #setupGallery() {
    this.gallery = this.querySelector('.mmx-fp__gallery');
    this.heroEl = this.querySelector('.mmx-fp__hero-swiper');
    this.thumbsEl = this.querySelector('.mmx-fp__thumbs');
    this.slides = Array.from(this.querySelectorAll('.mmx-fp__hero-swiper .swiper-slide'));
    this.thumbs = Array.from(this.querySelectorAll('.mmx-fp__thumb'));
    this.mediaIndex = 0;

    if (this.slides.length <= 1) return;

    const { signal } = this.#listeners;

    // Plain click handlers rather than relying on Swiper's `tap` alone: `tap`
    // only fires for pointer input, and these are buttons that also have to
    // answer Enter/Space. After a drag, Swiper swallows the click in its capture
    // phase, so dragging the strip doesn't also select a thumb.
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener(
        'click',
        () => {
          this.#goToMedia(index);
          const slide = thumb.closest('.swiper-slide');
          if (slide) this.#bringThumbIntoView(slide);
        },
        { signal }
      );
    });

    this.querySelector('.mmx-fp__arrow--prev')?.addEventListener('click', () => this.#stepMedia(-1), { signal });
    this.querySelector('.mmx-fp__arrow--next')?.addEventListener('click', () => this.#stepMedia(1), { signal });

    loadSwiper()
      .then((Swiper) => {
        if (signal.aborted || !this.isConnected) return;
        this.#initSwipers(Swiper);
      })
      .catch((error) => {
        if (signal.aborted) return;
        console.warn('[mmx-featured-product] Swiper unavailable, using static gallery.', error);
        this.gallery?.classList.add('is-static');
      });
  }

  /** @param {typeof import('swiper').Swiper} Swiper */
  #initSwipers(Swiper) {
    this.#destroySwipers();

    // Whatever was picked before the script arrived is carried over.
    const initialSlide = this.mediaIndex;
    if (this.heroEl) {
      const wrapper = this.heroEl.querySelector('.swiper-wrapper');
      if (wrapper instanceof HTMLElement) wrapper.style.transform = '';
    }

    if (this.thumbsEl) {
      const gap = getComputedStyle(this.thumbsEl).getPropertyValue('--mmx-fp-thumb-gap').trim() || '2.0514%';
      this.#thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 3,
        spaceBetween: gap,
        watchSlidesProgress: true,
        initialSlide: Math.max(0, initialSlide - 2),
      });
    }

    if (this.heroEl) {
      this.#heroSwiper = new Swiper(this.heroEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        grabCursor: true,
        initialSlide,
        // Slides sit side by side and clipped, so a lazy neighbour wouldn't start
        // loading until it was dragged into view. Swiper un-lazies the next and
        // previous slides ahead of time instead.
        lazyPreloadPrevNext: 1,
        thumbs: this.#thumbsSwiper ? { swiper: this.#thumbsSwiper } : undefined,
        on: {
          slideChange: (swiper) => this.#syncMedia(swiper.activeIndex),
        },
      });
    }

    this.#syncMedia(this.#heroSwiper?.activeIndex ?? initialSlide);
  }

  #destroySwipers() {
    // Hero first: its thumbs module holds a reference to the thumbs instance.
    this.#heroSwiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#heroSwiper = null;
    this.#thumbsSwiper = null;
  }

  /**
   * Arrows wrap last <-> first, as they did before the slider. That's done here
   * rather than with Swiper's `rewind`, which would also make a swipe past the
   * last photo jump back to the first — mm-bundle-builder stops at the ends.
   */
  #stepMedia(direction) {
    const count = this.slides.length;
    this.#goToMedia((this.mediaIndex + direction + count) % count);
  }

  #goToMedia(index) {
    if (index < 0 || index >= this.slides.length) return;

    if (this.#heroSwiper) {
      // `slideChange` does the syncing; it doesn't fire for the current slide.
      this.#heroSwiper.slideTo(index);
      return;
    }

    // Static fallback: shift the hero track and keep the picked thumb on screen.
    const wrapper = this.heroEl?.querySelector('.swiper-wrapper');
    if (wrapper instanceof HTMLElement) wrapper.style.transform = `translate3d(${-index * 100}%, 0, 0)`;

    const thumb = this.thumbs[index];
    if (thumb && this.thumbsEl && this.gallery?.classList.contains('is-static')) {
      // `scrollIntoView` would also scroll the page on mobile, so the track is
      // scrolled directly.
      const slide = thumb.closest('.swiper-slide') || thumb;
      const target = slide.offsetLeft - (this.thumbsEl.clientWidth - slide.clientWidth) / 2;
      this.thumbsEl.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    this.#syncMedia(index);
  }

  /** Mirrors the active slide onto the hero's aria state and the thumb buttons. */
  #syncMedia(index) {
    this.mediaIndex = index;

    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  /**
   * Same rule as mm-bundle-builder: after a thumb is picked, slide the strip so
   * that thumb is fully visible and, where there is one, the next thumb peeks in
   * — so the shopper can tell there are more to see.
   * @param {Element} slide
   */
  #bringThumbIntoView(slide) {
    const thumbsSwiper = this.#thumbsSwiper;
    if (!thumbsSwiper || thumbsSwiper.destroyed || !slide.parentNode) return;

    const siblings = slide.parentNode.children;
    const idx = Array.prototype.indexOf.call(siblings, slide);
    if (idx < 0) return;

    const viewport = thumbsSwiper.el.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const notFullyVisible = rect.left < viewport.left - 1 || rect.right > viewport.right + 1;
    const nextSlide = siblings[idx + 1];
    const prevSlide = siblings[idx - 1];
    const nextHidden = !!(nextSlide && nextSlide.getBoundingClientRect().right > viewport.right + 1);
    const prevHidden = !!(prevSlide && prevSlide.getBoundingClientRect().left < viewport.left - 1);

    if (notFullyVisible || nextHidden) {
      thumbsSwiper.slideTo(idx);
    } else if (prevHidden) {
      const perView = Number(thumbsSwiper.params.slidesPerView) || 1;
      thumbsSwiper.slideTo(Math.max(0, idx - (perView - 1)));
    }
  }

  /* -------------------------------- Bundle -------------------------------- */

  #setupBundle() {
    this.rows = Array.from(this.querySelectorAll('.mmx-fp__row'));
    this.counterOutput = this.querySelector('[data-bundle-count]');
    this.tiers = Array.from(this.querySelectorAll('.mmx-fp__tier'));
    // Index-aligned with `this.tiers`: the dots live in the track behind the
    // columns, so the reached state has to be mirrored onto them by position.
    this.tierDots = Array.from(this.querySelectorAll('.mmx-fp__tier-dot'));
    this.priceEl = this.querySelector('[data-mmx-fp-price]');
    this.savings = this.querySelector('.mmx-fp__savings');
    this.errorOutput = this.querySelector('.mmx-fp__error');
    this.cta = this.querySelector('.mmx-fp__cta');

    const { signal } = this.#listeners;

    for (const row of this.rows) {
      row.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-action]') : null;
        if (!button || !row.contains(button)) return;

        const current = Number(row.dataset.quantity) || 0;
        const next = { add: 1, increase: current + 1, decrease: current - 1 }[button.dataset.action];
        if (next == null) return;

        this.#setRowQuantity(row, Math.max(0, next));
      }, { signal });
    }

    this.cta?.addEventListener('click', () => this.#addBundleToCart(), { signal });

    this.#render();
  }

  #setRowQuantity(row, quantity) {
    row.dataset.quantity = String(quantity);
    this.#render();
  }

  get #totalQuantity() {
    return this.rows.reduce((total, row) => total + (Number(row.dataset.quantity) || 0), 0);
  }

  get #totalPrice() {
    return this.rows.reduce(
      (total, row) => total + (Number(row.dataset.price) || 0) * (Number(row.dataset.quantity) || 0),
      0
    );
  }

  #render() {
    for (const row of this.rows) {
      const quantity = Number(row.dataset.quantity) || 0;
      const stepper = row.querySelector('.mmx-fp__stepper');
      const add = row.querySelector('.mmx-fp__add');
      const output = row.querySelector('[data-quantity-output]');

      row.classList.toggle('is-selected', quantity > 0);
      if (output) output.textContent = String(quantity);
      if (stepper) stepper.hidden = quantity === 0;
      if (add instanceof HTMLElement) add.hidden = quantity > 0;
    }

    const total = this.#totalQuantity;
    if (this.counterOutput) this.counterOutput.textContent = String(total);

    this.tiers.forEach((tier, index) => {
      const threshold = Number(tier.dataset.tierQuantity) || 0;
      const reached = threshold > 0 && total >= threshold;
      tier.classList.toggle('is-reached', reached);
      this.tierDots[index]?.classList.toggle('is-reached', reached);
    });

    this.#renderTierCaptions(total);
    this.#renderSavings(total);
    this.#renderPrice(total);
    this.#renderCta(total);
  }

  /**
   * Savings tiers as `{ quantity, percent, amount }`, lowest quantity first.
   * `amount` is the cents off each jar (0: use `percent`). They
   * come from the Savings tiers block; without one (or with no tier offering
   * anything), the section's own discount setting stands in as a single tier.
   */
  get #priceTiers() {
    const tiers = this.tiers
      .map((tier) => ({
        quantity: Number(tier.dataset.tierQuantity) || 0,
        percent: Number(tier.dataset.tierDiscount) || 0,
        amount: Number(tier.dataset.tierAmount) || 0,
      }))
      .filter((tier) => tier.quantity > 0 && (tier.percent > 0 || tier.amount > 0));

    if (tiers.length === 0) {
      const percent = Number(this.dataset.discountPercentage) || 0;
      if (percent > 0) tiers.push({ quantity: Number(this.dataset.discountMinQuantity) || 1, percent, amount: 0 });
    }

    return tiers.sort((a, b) => a.quantity - b.quantity);
  }

  /**
   * Prices the pack at a tier (none: full price).
   *
   * An amount off tier takes its amount off every jar in the pack, the way a
   * Shopify "amount off each item" discount does (6 or 7 jars at $2.12 off
   * each, until the 9+ tier takes over). A jar never goes below free.
   *
   * A percentage tier covers exactly its own quantity of jars; any jars past it
   * are at full price until the next tier is reached (6 jars at 10% off + a 7th
   * at $9.99, then 9 jars at the 9+ price). With mixed prices the dearest jars
   * go into the tier, so the shopper gets the most out of it. Priced the way
   * Shopify discounts them: per unit, with each unit's discount rounded down to
   * the cent. Keep in step with #repriceTiers in mmx-product-information.js.
   * @param {{ quantity: number, percent: number, amount: number } | undefined} tier
   */
  #packTotal(tier) {
    const jars = this.rows
      .flatMap((row) => Array(Number(row.dataset.quantity) || 0).fill(Number(row.dataset.price) || 0))
      .sort((a, b) => b - a);
    const fullPrice = (list) => list.reduce((total, price) => total + price, 0);

    if (!tier || jars.length < tier.quantity) return fullPrice(jars);

    if (tier.amount > 0) return jars.reduce((total, price) => total + Math.max(price - tier.amount, 0), 0);

    const inTier = jars.slice(0, tier.quantity);
    const rest = fullPrice(jars.slice(tier.quantity));

    return inTier.reduce((total, price) => total + price - Math.floor((price * tier.percent) / 100), 0) + rest;
  }


  /**
   * Fills `{percent}` (and `{amount}`) in the tier cards' text. A percentage
   * tier is its own figure; an amount off tier is worked out against the
   * average jar picked, or, before any are, the average jar in the bundle
   * (the server can only use the section's product, normally the same price).
   * @param {number} total - jars picked
   */
  #renderTierCaptions(total) {
    const rowsWithPrice = this.rows.filter((row) => Number(row.dataset.price) > 0);
    const averageJar =
      total > 0
        ? this.#totalPrice / total
        : rowsWithPrice.reduce((sum, row) => sum + Number(row.dataset.price), 0) / (rowsWithPrice.length || 1);

    this.tiers.forEach((tier) => {
      const caption = tier.querySelector('.mmx-fp__tier-caption');
      const template = caption instanceof HTMLElement ? caption.dataset.template : undefined;
      if (!caption || !template) return;

      const offer = {
        quantity: Number(tier.dataset.tierQuantity) || 0,
        percent: Number(tier.dataset.tierDiscount) || 0,
        amount: Number(tier.dataset.tierAmount) || 0,
      };
      const percent = offer.amount > 0 && offer.quantity > 0 ? this.#tierOffer(offer, averageJar).percent : offer.percent;

      caption.textContent = template
        .replaceAll('{percent}', String(percent))
        .replaceAll('{amount}', offer.amount > 0 ? this.#money(offer.amount) : '{amount}');
    });
  }

  /**
   * What a tier is worth for the nudge, priced on the pack's average jar:
   * `percent` saved per jar (floored, so an amount off tier never oversells)
   * and `price`, what the tier's quantity of jars costs.
   * @param {{ quantity: number, percent: number, amount: number }} tier
   * @param {number} averageJar - cents
   */
  #tierOffer(tier, averageJar) {
    if (tier.amount > 0) {
      const off = Math.min(tier.amount, averageJar);
      const percent = averageJar > 0 ? Math.floor((off * 100) / averageJar) : 0;
      return { percent, price: Math.round((averageJar - off) * tier.quantity) };
    }
    const unit = averageJar - Math.floor((averageJar * tier.percent) / 100);
    return { percent: tier.percent, price: Math.round(unit * tier.quantity) };
  }

  /**
   * What the cart will charge for the pack, mirroring the store's discounts:
   * the cheapest tier reached prices every jar (#packTotal), then the order
   * discount comes off the pack once it clears its minimum. Shopify rounds each
   * discount down to the cent, so the order discount is floored too: 9 jars at
   * $6.99 -> $5.95 each (15%) -> $53.55 -> $48.20 with 10% off over $50.
   *
   * Cheapest rather than highest: with amount off and percentage tiers mixed a
   * bigger tier can work out dearer (9 jars at $9.99: $3 off each from 6+ is
   * $62.91, 10% off from 9+ is $81.00), and Shopify, given competing
   * discounts, applies the best one. The nudge names the next tier that saves
   * anything.
   */
  #packPrice(total) {
    const el = this.priceEl instanceof HTMLElement ? this.priceEl : null;
    const tiers = this.#priceTiers;
    const subtotal = this.#totalPrice;

    let reached;
    let tierTotal = subtotal;
    for (const tier of tiers) {
      if (total < tier.quantity) break;
      const price = this.#packTotal(tier);
      // Ties go to the bigger tier, the one the shopper has worked towards.
      if (price <= tierTotal) {
        reached = tier;
        tierTotal = price;
      }
    }

    const averageJar = total > 0 ? subtotal / total : 0;
    const next = tiers.find((tier) => total < tier.quantity && this.#tierOffer(tier, averageJar).percent > 0);

    const orderPercent = Number(el?.dataset.orderPercent) || 0;
    const orderMinimum = Number(el?.dataset.orderMinimum) || 0;
    const orderApplies = orderPercent > 0 && total > 0 && tierTotal >= orderMinimum;
    const orderDiscount = orderApplies ? Math.floor((tierTotal * orderPercent) / 100) : 0;

    return { reached, next, subtotal, packTotal: tierTotal - orderDiscount, orderApplies, orderPercent, orderMinimum };
  }

  #renderPrice(total) {
    const el = this.priceEl;
    if (!(el instanceof HTMLElement)) return;

    const meta = el.querySelector('[data-price-meta]');
    const totalOutput = el.querySelector('[data-price-total]');
    const was = el.querySelector('[data-price-was]');
    const badge = el.querySelector('[data-price-badge]');
    const note = el.querySelector('[data-price-note]');
    const nudge = el.querySelector('[data-price-nudge]');

    const { reached, next, subtotal, packTotal, orderApplies, orderPercent, orderMinimum } = this.#packPrice(total);
    const saving = subtotal - packTotal;
    // Rounded rather than floored: per-jar cent rounding leaves the 5% tier at
    // 4.86% actually saved, and the badge should still agree with the tier card.
    // An amount off tier has no percentage on its card, so it is floored and
    // never claims more than the pack saves.
    const ratio = subtotal > 0 ? (saving * 100) / subtotal : 0;
    const percent = reached && reached.amount > 0 ? Math.floor(ratio) : Math.round(ratio);

    if (meta) {
      if (total === 0) {
        meta.textContent = el.dataset.emptyText || '';
      } else {
        const count = ((total === 1 ? el.dataset.countOne : el.dataset.countOther) || '{count}').replace(
          '{count}',
          String(total)
        );
        const unit = (el.dataset.unitText || '').replace('{unit}', this.#money(Math.round(packTotal / total)));
        meta.textContent = [count, unit].filter(Boolean).join(' · ');
      }
    }

    if (totalOutput) totalOutput.textContent = this.#money(packTotal);

    if (was instanceof HTMLElement) {
      was.hidden = saving <= 0;
      was.textContent = saving > 0 ? this.#money(subtotal) : '';
    }

    if (badge instanceof HTMLElement) {
      badge.hidden = saving <= 0 || percent <= 0;
      badge.textContent = (el.dataset.badgeText || '')
        .replace('{percent}', String(percent))
        .replace('{saving}', this.#money(saving));
    }

    if (note instanceof HTMLElement) {
      const text = orderApplies
        ? (el.dataset.orderText || '')
            .replace('{percent}', String(orderPercent))
            .replace('{minimum}', this.#money(orderMinimum).replace(/[.,]00(?!\d)/, ''))
        : '';
      note.textContent = text;
      note.hidden = !text;
    }

    if (nudge instanceof HTMLElement) {
      let text = '';
      if (total > 0 && next) {
        const offer = this.#tierOffer(next, subtotal / total);
        text = (el.dataset.nudgeText || '')
          .replace('{remaining}', String(next.quantity - total))
          .replace('{percent}', String(offer.percent))
          .replace('{price}', this.#money(offer.price))
          .replace('{quantity}', String(next.quantity));
      } else if (total > 0 && reached) {
        text = el.dataset.bestText || '';
      }
      nudge.textContent = text;
      nudge.hidden = !text;
      nudge.classList.toggle('is-best', !next && !!reached);
    }

    el.classList.toggle('is-saving', saving > 0);
  }

  /**
   * The CTA waits for the pack to reach the Add to cart block's minimum. With no
   * bundle rows it adds the section's own product, so there's nothing to wait for.
   */
  #renderCta(total) {
    const cta = this.cta;
    if (!(cta instanceof HTMLButtonElement) || this.#submitting) return;

    const minimum = Number(cta.dataset.minimumQuantity) || 1;
    const ready = this.rows.length === 0 || total >= minimum;
    cta.disabled = !ready;
    cta.classList.toggle('is-waiting', !ready);
  }

  #renderSavings(total) {
    const savings = this.savings;
    if (!savings) return;

    const percentage = Number(this.dataset.discountPercentage) || 0;
    const minimum = Number(this.dataset.discountMinQuantity) || 1;

    if (percentage <= 0 || total < minimum) {
      savings.hidden = true;
      return;
    }

    const subtotal = this.#totalPrice;
    const discounted = Math.round(subtotal * (1 - percentage / 100));

    savings.hidden = false;
    const template = this.dataset.savingsText || '';
    savings.textContent = template
      .replace('{percent}', String(percentage))
      .replace('{code}', this.dataset.discountCode || '')
      .replace('{total}', this.#money(discounted))
      .replace('{was}', this.#money(subtotal))
      .replace('{saving}', this.#money(subtotal - discounted));
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /* ----------------------------- Add to cart ----------------------------- */

  #collectItems() {
    const items = this.rows
      .filter((row) => row.dataset.variantId && (Number(row.dataset.quantity) || 0) > 0)
      .map((row) => ({ id: Number(row.dataset.variantId), quantity: Number(row.dataset.quantity) }));

    if (items.length > 0) return items;

    // Once the bundle block has rows the shopper is meant to pick the jars, so
    // an empty selection prompts instead of quietly adding something they
    // didn't choose. With no rows at all the CTA falls back to the section's
    // own product and behaves like a plain add-to-cart button.
    if (this.rows.length > 0) return [];

    const fallback = Number(this.dataset.defaultVariantId);
    return fallback ? [{ id: fallback, quantity: 1 }] : [];
  }

  async #addBundleToCart() {
    if (this.#submitting) return;

    const items = this.#collectItems();
    if (items.length === 0) {
      this.#showError(this.dataset.emptyError || '');
      return;
    }

    this.#submitting = true;
    this.cta?.setAttribute('aria-busy', 'true');
    if (this.cta instanceof HTMLButtonElement) this.cta.disabled = true;
    this.#hideError();

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    // Dispatched before the request so the cart drawer can open optimistically;
    // it stays in its loading state until the promise below resolves.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSectionIds() }),
      }).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#showError(response.description || response.message || '');

        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'mmx-featured-product', itemCount: totalQuantity },
        });
        return;
      }

      await this.#applyDiscount();

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'mmx-featured-product',
          itemCount: totalQuantity,
          sections: response.sections,
        },
      });

      this.#resetRows();
      this.#showAdded();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.dataset.genericError || '');
    } finally {
      this.#submitting = false;
      this.cta?.removeAttribute('aria-busy');
      this.#renderCta(this.#totalQuantity);
    }
  }

  /**
   * `/cart/update.js` replaces the whole discount list, so the codes already on
   * the cart have to be sent back alongside the new one.
   */
  async #applyDiscount() {
    const code = (this.dataset.discountCode || '').trim();
    if (!code) return;

    const cart = await this.#fetchCartJson();
    const existing = (cart?.discount_codes ?? []).map((discount) => discount.code).filter(Boolean);

    if (existing.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) return;

    await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...existing, code].join(',') }),
    });
  }

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  #fetchCartJson() {
    return fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    return this.#fetchCartJson();
  }

  #resetRows() {
    for (const row of this.rows) row.dataset.quantity = '0';
    this.#render();
  }

  /** Swap the CTA label for the tick for a moment once the pack is in the cart. */
  #showAdded() {
    if (!this.cta) return;
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
    this.cta.dataset.added = 'true';
    this.#addedTimeout = setTimeout(() => this.cta?.removeAttribute('data-added'), 1600);
  }

  #showError(message) {
    if (!this.errorOutput || !message) return;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.errorOutput.textContent = message;
    this.errorOutput.hidden = false;
    this.#errorTimeout = setTimeout(() => this.#hideError(), 8000);
  }

  #hideError() {
    if (!this.errorOutput) return;
    this.errorOutput.hidden = true;
    this.errorOutput.textContent = '';
  }
}

if (!customElements.get('mmx-featured-product')) {
  customElements.define('mmx-featured-product', MmxFeaturedProduct);
}
